import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogApi, IDialog } from '../../api/DialogApi';
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';

export interface SelectNextDialogOptionBase {
    id: number;
    button_text: string;
    response_text: string;
    next_dialog_id: number;
}

interface DialogPlanLlmPromptSelectNextDialogOptionsProps<T extends SelectNextDialogOptionBase> {
    nextDialogList: T[];
    setNextDialogList: React.Dispatch<React.SetStateAction<T[]>>;
    createNextDialog: () => T;
}

function DialogPlanLlmPromptSelectNextDialogOptions<T extends SelectNextDialogOptionBase>({ nextDialogList, setNextDialogList, createNextDialog }: DialogPlanLlmPromptSelectNextDialogOptionsProps<T>) {
    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const [dialogList, setDialogList] = React.useState<IDialog[]>([]);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [dialogIndexToDelete, setDialogIndexToDelete] = React.useState<number | null>(null);

    React.useEffect(() => {
        DialogApi.getAll()
            .then(response => {
                if (response.status < 300) return response.data;
                throw new Error("Unable to DialogApi.getAll() " + response.status);
            })
            .then(list => setDialogList(list))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [history, location, setErrorMessage]);

    const handleAddNextDialog = () => {
        setNextDialogList(current => [...current, createNextDialog()]);
    };

    const handleNextDialogChange = (index: number, field: keyof SelectNextDialogOptionBase, value: string | number) => {
        setNextDialogList(current =>
            current.map((nextDialog, i) => {
                if (i === index) return { ...nextDialog, [field]: value };
                return nextDialog;
            })
        );
    };

    const handleDeleteNextDialog = (index: number) => {
        setDialogIndexToDelete(index);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (dialogIndexToDelete !== null) {
            setNextDialogList(current => current.filter((_, index) => index !== dialogIndexToDelete));
        }
        setDialogIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setDialogIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    return (<>
        {nextDialogList.map((nextDialog, index) => (
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }} key={index}>
                <h3>Next Dialog Option</h3>
                <TextField
                    label="Button text"
                    value={nextDialog.button_text}
                    required
                    onChange={(e) => handleNextDialogChange(index, 'button_text', e.target.value)}
                />
                <TextField
                    label="Response Text"
                    value={nextDialog.response_text}
                    required
                    onChange={(e) => handleNextDialogChange(index, 'response_text', e.target.value)}
                />
                <FormControl fullWidth>
                    <InputLabel>Select Dialog</InputLabel>
                    <Select
                        label="Select Dialog"
                        value={nextDialog.next_dialog_id}
                        required
                        onChange={(e) => handleNextDialogChange(index, 'next_dialog_id', e.target.value as number)}
                    >
                        <MenuItem value={0} disabled>Select a dialog</MenuItem>
                        {dialogList.map((dialog) => (
                            <MenuItem key={dialog.id} value={dialog.id} sx={{ fontWeight: dialog.is_hidden ? 200 : 400, opacity: dialog.is_hidden ? 0.4 : 1 }}>
                                {dialog.is_hidden && "[Hidden]"} {`${dialog.title} :: ${dialog.subtitle}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => handleDeleteNextDialog(index)}>
                    Delete
                </Button>
            </Stack>
        ))}
        <Button variant="outlined" onClick={handleAddNextDialog}>
            Add Next Dialog Option
        </Button>
        <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete this dialog option {dialogIndexToDelete !== null && (<>{nextDialogList[dialogIndexToDelete].button_text} - {nextDialogList[dialogIndexToDelete].response_text}</>)} ?
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default DialogPlanLlmPromptSelectNextDialogOptions;
